import {useMemo} from 'react';
import {UnifiedFeedItem, UnifiedFeedPage} from '@/types/conversationMessage';
import unifiedFeedFetchers from '@/api/fetchers/conversations/unifiedFeed';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

const DEFAULT_STALE_TIME = 30_000;

export const UNIFIED_MESSAGES_BASE_QUERY_KEY = 'unifiedMessages';

export const unifiedMessagesQueryKey = (
  conversationId: string | null,
  campaignId?: string
) => [UNIFIED_MESSAGES_BASE_QUERY_KEY, conversationId, campaignId] as const;

function useUnifiedMessages(
  conversationId: string | null,
  campaignId?: string
): {
  items: UnifiedFeedItem[];
  firstUnreadMessageId: string | null;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
} {
  const response = useInfiniteReactQuery<UnifiedFeedPage>({
    queryKey: unifiedMessagesQueryKey(conversationId, campaignId),
    queryFn: ({pageParam}: {pageParam?: string}) => {
      if (!conversationId) {
        return Promise.reject(new Error('conversationId required'));
      }
      return unifiedFeedFetchers.list(conversationId, pageParam, campaignId);
    },
    enabled: !!conversationId,
    // next_cursor in the response is the created_at of the 25th raw message before
    // expansion. Do NOT infer cursor from the last item — version_thread items are
    // positioned by latest_message_at, not a raw message timestamp.
    getNextPageParam: (lastPage: UnifiedFeedPage) =>
      lastPage.nextCursor ?? undefined,
    staleTime: DEFAULT_STALE_TIME,
    refetchOnWindowFocus: true,
  });

  const items = useMemo(() => {
    if (!response.data) {
      return [];
    }
    return response.data.pages.flatMap((page) => page.items);
  }, [response.data]);

  return {
    items,
    firstUnreadMessageId: response.data?.pages[0]?.firstUnreadMessageId ?? null,
    fetchNextPage: response.fetchNextPage,
    hasNextPage: response.hasNextPage ?? false,
    isFetchingNextPage: response.isFetchingNextPage,
    isLoading: !conversationId || response.isLoading,
    isSuccess: response.isSuccess,
    isError: response.isError,
  };
}

export default useUnifiedMessages;
